import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getShoppingCenters } from 'hooks/useUser';
import { useTableChangeFilterHook } from 'hooks/useTableChangeFilterHook';
import { useFilter } from 'hooks/useFilter';
import BoardDocsApi from 'libs/api/boardDocs';
import BoardDocPromotionsAdapter from './boardDocs-promotions.adapter';
import { getShops } from '../../../../hooks/useShops';
import UIHelper from '../../../../helpers/UIHelper';

const defaultState = {
  promotions: [],
  pagination: {
    current: 1,
    pageSize: 10,
    position: ['bottomCenter'],
    hideOnSinglePage: true,
    showSizeChanger: false,
  },
  sorting: {
    field: '',
    order: '',
  },
};

const api = new BoardDocsApi();

const getInitialState = () => defaultState;

export const useBoardDocsPromotions = () => {
  const { t } = useTranslation();
  const { filterData } = useFilter();
  const onChangeTable = useTableChangeFilterHook();
  const { handleChangeFilters, handleCloseFilter, searchParams } = useFilter();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(getInitialState());

  const fetchData = async () => {
    try {
      const params = {
        per: data.pagination.pageSize,
        page: data.pagination.current,
        ...Object.fromEntries([...searchParams]),
      };
      if (data.sorting.order) {
        params.sortable_column = data.sorting.field;
        params.direction = data.sorting.order;
      }
      const {
        data: { promotions, pagination = {} },
      } = await api.get('promotions', { params });
      setData((s) => ({
        ...s,
        promotions: [...BoardDocPromotionsAdapter(promotions || [])],
        pagination: {
          ...defaultState.pagination,
          ...pagination,
          total: pagination?.total_count,
        },
      }));
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchParams, data.pagination.current, data.sorting.order]);

  const columns = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        sorter: () => {},
        dataIndex: 'shopping_centers.title',
      },
      {
        title: t('Insegna'),
        dataIndex: 'company.title',
        width: 150,
        sorter: () => {},
      },
      {
        title: t('Titolo'),
        dataIndex: 'promotion_translations.title',
        sorter: () => {},
      },
      {
        title: t('Data inizio promozione'),
        dataIndex: 'from',
        sorter: () => {},
        render: (row) => UIHelper.formatedDate(row),
      },
      {
        title: t('Data fine promozione'),
        dataIndex: 'to',
        sorter: () => {},
        render: (row) => UIHelper.formatedDate(row),
      },
    ],
    [t],
  );

  const filters = useMemo(
    () => [
      {
        title: t('Outlet Village'),
        type: 'select',
        name: 'by_shopping_center',
        values: filterData?.shopping_centers?.map((shopping_center) => ({
          title: shopping_center.title,
          value: shopping_center.value,
        })),
      },
      {
        title: t('Insegna'),
        type: 'select',
        name: 'by_company',
        isSearch: true,
        values: filterData?.companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
        inactiveValues: filterData?.inactive_companies?.map((company) => ({
          title: company.title,
          value: company.value,
        })),
      },
      {
        title: t('Cerca'),
        type: 'input',
        name: 'by_title',
      },
      {
        title: t('Date range'),
        type: 'range',
        name: 'dateRange',
        keys: ['by_date_from', 'by_date_to'],
      },
    ],
    [t, filterData],
  );

  const onChangeTableFilter = useCallback(
    (paginate, sort, filter) => {
      onChangeTable(setData, paginate, sort, filter);
    },
    [onChangeTable],
  );

  return {
    data,
    loading,
    handleChangeFilters,
    handleCloseFilter,
    columns,
    onChangeTableFilter,
    filters,
  };
};
